import { ButtonSecondary } from "@nova/component-btn"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { convertDistance } from "@nova/format"
import * as Icons from "@nova/component-icons"
import {
    ColorSwatchCategory,
    DistanceUnit,
    formattedDistanceUnit,
    hexColorSwatches,
    HotspotWithDistance,
    zIndex,
} from "@nova/constants"
import { Filters, InfraredFlight, Range } from "@nova/protobuf"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { convertRGBAArrayToHexString } from "@nova/util-strings"
import classNames from "classnames"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import {
    selectIdRenaming,
    selectWorkspaceDistanceUnit,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActiveFirePerimeterIds } from "features/entity-caching/fire-perimeters/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { clearSelection, setActiveFeatureId } from "panels/content/side-panel/slice.new"
import { usePluginState } from "panels/content/side-panel/usePluginState"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { InspectorPanelAccordionKeyValueToggle } from "../../accordion/InspectorPanelAccordionKeyValueToggle"
import { ColorPalette } from "../../icons/TODO-put-in-package/ColorPalette"
import {
    SecondaryFlyoutMenu,
    SecondaryFlyoutMenuContentItem,
} from "../../icons/TODO-put-in-package/SecondaryFlyoutMenu"
import { bufferColors, perimeterDistanceOptions } from "../constants"
import { InfraredFlightUpdates } from "../InspectorPanelHotspotLayer"

const bufferColorSwatches = hexColorSwatches[ColorSwatchCategory.Hotspot].map((hex) =>
    encodeColorToNumber(hex)
)

export const InspectorPanelHotspotLayerFiltersDistanceToPerimeter = ({
    flight,
    updateFn,
    triggerResize,
    filteredHotspots,
}: {
    flight: InfraredFlight
    updateFn: (updates: InfraredFlightUpdates) => void
    triggerResize: () => void
    filteredHotspots?: HotspotWithDistance[]
}) => {
    const dispatch = useAppDispatch()

    const projectHasFirePerimeters = !!useAppSelector(selectActiveFirePerimeterIds).length
    const idRenaming = useAppSelector(selectIdRenaming())
    const [openDropdownId, setOpenDropdownId] = useState<string>()

    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const distanceUnit = (flight.perimeterDistance?.unit as DistanceUnit) || workspaceDistanceUnit
    const unitAbbreviation = formattedDistanceUnit[distanceUnit].abbrev

    const { setSelectedPlugin } = usePluginState()

    const distances = useMemo(() => {
        if (!flight.perimeterDistance) return undefined

        const perimeterDistanceRange = [...flight.perimeterDistance.range]

        const hotspotCountPerBuffer = new Map<number, number>()
        if (filteredHotspots) {
            filteredHotspots.forEach((hotspot) => {
                if (!hotspot.color) return
                const colorNumber = encodeColorToNumber(convertRGBAArrayToHexString(hotspot.color))
                const count = hotspotCountPerBuffer.get(colorNumber) || 0
                hotspotCountPerBuffer.set(colorNumber, count + 1)
            })
        }

        return perimeterDistanceRange
            .sort((a, b) => a.distance - b.distance)
            .map((buffer) => {
                return {
                    ...buffer,
                    distance: convertDistance(buffer.distance, DistanceUnit.Meters, distanceUnit),
                    bufferCount: filteredHotspots
                        ? (hotspotCountPerBuffer.get(buffer.color) ?? 0)
                        : 0,
                }
            })
    }, [distanceUnit, filteredHotspots, flight.perimeterDistance])

    const updatePerimeterDistance = useCallback(
        (changes: { range?: { distance: number; color: number }[]; unit?: DistanceUnit }) => {
            const currentUnit = (flight.perimeterDistance?.unit as DistanceUnit) ?? distanceUnit
            const nextUnit = changes.unit ?? currentUnit
            const currentRange = changes.range ?? flight.perimeterDistance?.range ?? []

            const convertedRange = currentRange.map((buffer) => {
                const actualDistanceInCurrentUnit = convertDistance(
                    buffer.distance,
                    DistanceUnit.Meters,
                    currentUnit
                )
                const newDistanceInMeters = convertDistance(
                    actualDistanceInCurrentUnit,
                    nextUnit,
                    DistanceUnit.Meters
                )

                return {
                    ...buffer,
                    distance: newDistanceInMeters ?? 0,
                }
            })

            const updates = {
                pointColor:
                    (changes.range &&
                        [...changes.range].sort((a, b) => a.distance - b.distance).at(0)?.color) ||
                    flight.pointColor,
                perimeterDistance: {
                    range: convertedRange || flight.perimeterDistance?.range || [],
                    unit: changes?.unit || distanceUnit,
                },
            }
            updateFn(updates)
        },
        [
            flight.perimeterDistance?.unit,
            flight.perimeterDistance?.range,
            flight.pointColor,
            distanceUnit,
            updateFn,
        ]
    )

    const createBuffer = useCallback(() => {
        if (distances && distances.length >= 3) return

        const oldDistancesInMeters =
            distances?.map((entry) => ({
                ...entry,
                distance: convertDistance(entry.distance, distanceUnit, DistanceUnit.Meters),
            })) || []
        const previousBuffer = oldDistancesInMeters?.at(-1)?.distance || 0
        const newBufferInMeters =
            previousBuffer + convertDistance(100, distanceUnit, DistanceUnit.Meters)
        const newColor = bufferColors[oldDistancesInMeters?.length || 0]

        updatePerimeterDistance({
            range: [
                ...oldDistancesInMeters,
                {
                    distance: newBufferInMeters,
                    color: encodeColorToNumber(newColor),
                },
            ],
            unit: distanceUnit,
        })
    }, [distances, updatePerimeterDistance, distanceUnit])

    const deleteBufferAtIndex = useCallback(
        (index: number) => {
            if (!distances || (distances && distances.length === 0)) return

            const oldDistancesInMeters =
                distances?.map((entry) => ({
                    ...entry,
                    distance: convertDistance(entry.distance, distanceUnit, DistanceUnit.Meters),
                })) || []

            const oldRange = JSON.parse(JSON.stringify(oldDistancesInMeters)) as Range[]
            const newRange = oldRange.filter((_, i) => i !== index)

            updatePerimeterDistance({ range: newRange })
        },
        [distances, distanceUnit, updatePerimeterDistance]
    )

    const editBufferAtIndex = useCallback(
        (index: number, updates: { buffer?: number; color?: number }) => {
            if (!distances || (distances && distances.length === 0)) return

            const oldDistancesInMeters =
                distances?.map((entry) => ({
                    ...entry,
                    distance: convertDistance(entry.distance, distanceUnit, DistanceUnit.Meters),
                })) || []

            const range = JSON.parse(JSON.stringify(oldDistancesInMeters)) as Range[]

            if (updates.buffer !== undefined)
                range[index].distance = convertDistance(
                    updates.buffer,
                    distanceUnit,
                    DistanceUnit.Meters
                )
            if (updates.color !== undefined) range[index].color = updates.color

            updatePerimeterDistance({ range })
        },
        [distances, distanceUnit, updatePerimeterDistance]
    )

    const flyoutContentItems: SecondaryFlyoutMenuContentItem[] = useMemo(
        () =>
            perimeterDistanceOptions.map((option) => ({
                id: option,
                title: formattedDistanceUnit[option].full,
                onClick: () => updatePerimeterDistance({ unit: option }),
                isSelected: option === distanceUnit,
            })),
        [updatePerimeterDistance, distanceUnit]
    )

    // This should only be called by the useEffect that listens to changes to `entity`!
    const [localFilters, setLocalFilters] = useState<Filters>(flight.filters || {})

    const localFiltersRef = useRef<Filters>(undefined)

    const updatePerimeterIsEnabled = useCallback(
        (isEnabled: boolean) => {
            if (!flight) return
            const newFilters = { ...localFilters, perimeter: isEnabled }
            updateFn({ filters: newFilters })
            setLocalFilters((prev) => ({ ...prev, perimeter: isEnabled }))
        },
        [flight, localFilters, updateFn]
    )

    /**
     * If the entity is changed externally (not via the Inspector Panel), this
     * will ensure that those changes update the Inspector Panel. Make sure
     * you follow the pattern below if new properties get added to InfraredFlight.
     */
    useEffect(() => {
        if (!flight) return

        // Update local sensitivity.
        const newFilters = flight.filters
        if (newFilters !== undefined && newFilters !== localFiltersRef.current) {
            setLocalFilters(newFilters)
            localFiltersRef.current = newFilters
        }
    }, [flight])

    return !projectHasFirePerimeters ? (
        <div className="flex flex-col gap-2">
            <h5 className="text-gray-0 select-none">Distance to Perimeter</h5>
            <ButtonSecondary
                className="w-full"
                title="Create a Fire Perimeter"
                onClick={() => {
                    setSelectedPlugin(NovaPluginIds.Perimeter)
                    dispatch(setActiveFeatureId({ feature: NovaPluginIds.Perimeter }))
                    dispatch(clearSelection())
                }}
                onMouseUp={(e) => e.stopPropagation()}
            />
        </div>
    ) : (
        <InspectorPanelAccordionKeyValueToggle
            keyTitle="Distance to Perimeter"
            isToggled={!!localFilters.perimeter && projectHasFirePerimeters}
            setIsToggled={() => updatePerimeterIsEnabled(!localFilters.perimeter)}
            disabled={!projectHasFirePerimeters}
        >
            <div>
                <div className="flex h-5 items-center">
                    <small className="text-gray-300 w-16 select-none">Buffer</small>
                    <small className="text-gray-300 ml-2 select-none">Range</small>

                    {/* Spacer */}
                    <div className="flex-grow" />

                    {/* Unit Dropdown */}
                    <div className="relative z-10">
                        {/* Dropdown Button */}
                        <div
                            className={classNames(
                                "rounded-md border hover:border-gray-500 flex items-center justify-center text-gray-0 pl-2 pr-0.5 group cursor-pointer",
                                {
                                    "border-transparent": openDropdownId !== "unit",
                                    "border-gray-500 bg-gray-500": openDropdownId === "unit",
                                }
                            )}
                            onClick={() => {
                                setOpenDropdownId(openDropdownId === "unit" ? undefined : "unit")
                                triggerResize()
                            }}
                        >
                            <small className="select-none">
                                {formattedDistanceUnit[distanceUnit].abbrev}
                            </small>
                            <Icons.SmallIcon
                                Icon={Icons.ArrowDropdown}
                                className={classNames("group-hover:text-gray-0", {
                                    "text-gray-300": openDropdownId !== "unit",
                                    "text-gray-0": openDropdownId === "unit",
                                })}
                            />
                        </div>

                        {/* Actual Dropdown */}
                        {openDropdownId === "unit" && (
                            <div
                                className="absolute right-0"
                                style={{ top: "calc(100% + 0.25rem)" }}
                            >
                                <SecondaryFlyoutMenu
                                    className="whitespace-nowrap"
                                    items={flyoutContentItems}
                                    onBlur={() => {
                                        setOpenDropdownId(undefined)
                                        triggerResize()
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Buffers/Ranges */}
                <div className="flex flex-col">
                    {distances?.map((entry, i) => {
                        const bufferId = `distance-to-perimeter-${i}`
                        const isCurrentRenaming = idRenaming === bufferId
                        const bufferPlaceholderWidth = `${isCurrentRenaming ? 7 : 4}rem`

                        return (
                            <div key={i} className="flex flex-col py-2 group/row gap-1">
                                <div className="flex items-center">
                                    {/* Buffer */}
                                    <Buffer
                                        id={bufferId}
                                        buffer={entry.distance}
                                        unitAbbreviation={unitAbbreviation}
                                        updateBuffer={(buffer) => editBufferAtIndex(i, { buffer })}
                                    />

                                    {/* Color and Range */}
                                    {idRenaming !== bufferId && (
                                        <>
                                            {/* Color */}
                                            <div className="relative">
                                                <Icons.SmallIcon
                                                    Icon={Icons.SquareSwatch}
                                                    style={{
                                                        color: decodeNumberToColor(entry.color),
                                                    }}
                                                    className={classNames(
                                                        "cursor-pointer hover:brightness-125",
                                                        {
                                                            "brightness-125":
                                                                openDropdownId === bufferId,
                                                        }
                                                    )}
                                                    onClick={() => {
                                                        setOpenDropdownId(
                                                            openDropdownId !== bufferId
                                                                ? bufferId
                                                                : undefined
                                                        )
                                                        triggerResize()
                                                    }}
                                                />

                                                {/* Color Palette */}
                                                {openDropdownId === bufferId && (
                                                    <ClickAwayListener
                                                        onClickAway={() =>
                                                            setOpenDropdownId(undefined)
                                                        }
                                                    >
                                                        <div
                                                            className="absolute top-full -left-[3.125rem]"
                                                            style={{
                                                                zIndex: zIndex.InspectorPanel + 50,
                                                                top: "calc(100% + 0.125rem)",
                                                            }}
                                                        >
                                                            <ColorPalette
                                                                swatches={bufferColorSwatches}
                                                                selectedColor={entry.color}
                                                                setSelectedColor={(color) =>
                                                                    editBufferAtIndex(i, { color })
                                                                }
                                                                setOpenDropdownId={() => {
                                                                    setOpenDropdownId(
                                                                        openDropdownId !== bufferId
                                                                            ? bufferId
                                                                            : undefined
                                                                    )
                                                                    triggerResize()
                                                                }}
                                                                triggerResize={triggerResize}
                                                            />
                                                        </div>
                                                    </ClickAwayListener>
                                                )}
                                            </div>

                                            {/* Buffer Range */}
                                            <div className="ml-2 flex items-center gap-1">
                                                <small className="mono text-gray-300 select-none leading-none">
                                                    {`${!i ? "-∞" : `${Math.round(distances[i - 1].distance)}`} to ${Math.round(entry.distance)} ${unitAbbreviation}`}
                                                </small>
                                            </div>

                                            {/* Spacer */}
                                            <div className="flex-grow" />

                                            {/* Delete Buffer Button */}
                                            <Icons.SmallIcon
                                                Icon={Icons.Minus}
                                                className="text-white invisible group-hover/row:visible cursor-pointer"
                                                onClick={() => deleteBufferAtIndex(i)}
                                            />
                                        </>
                                    )}
                                </div>

                                {idRenaming !== bufferId && (
                                    <div className="flex items-center">
                                        <div style={{ width: bufferPlaceholderWidth }} />
                                        <div style={{ width: "1rem" }} />
                                        <small className="mono text-gray-300 select-none ml-3 leading-none">
                                            {filteredHotspots &&
                                                `${entry.bufferCount} Hotspot${entry.bufferCount === 1 ? "" : "s"}`}
                                        </small>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {(distances?.length || 0) < 3 && (
                    <ButtonSecondary
                        LeftIcon={Icons.Plus}
                        title="Add Buffer"
                        className="w-full mt-3"
                        onClick={() => createBuffer()}
                    />
                )}
            </div>
        </InspectorPanelAccordionKeyValueToggle>
    )
}

const Buffer = ({
    id,
    buffer,
    unitAbbreviation,
    updateBuffer,
}: {
    id: string
    buffer: number
    unitAbbreviation: string
    updateBuffer: (buffer: number) => void
}) => {
    const dispatch = useAppDispatch()
    const isRenaming = useAppSelector(selectIdRenaming()) === id
    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isRenaming) ref.current?.focus()
    }, [isRenaming])

    return (
        <div
            className="flex items-center"
            style={{ width: `${isRenaming ? 7 : 4}rem`, minHeight: "2rem" }}
        >
            <RenamableText
                ref={ref}
                Tag="small"
                title={`${isRenaming ? buffer : Math.round(buffer)}${!isRenaming ? ` ${unitAbbreviation}` : ""}`}
                id={id}
                isRenaming={isRenaming}
                className={classNames("mono cursor-text", {
                    "text-gray-100 hover:text-gray-0": !isRenaming,
                    "text-gray-0": isRenaming,
                })}
                onClick={() => dispatch(setIdRenaming(id))}
                onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                onConfirmRenaming={(_, value) => {
                    updateBuffer(Number(value))
                    dispatch(setIdRenaming(undefined))
                }}
                sanitizer={(value) => value.replace(/\D/g, "")}
            />
        </div>
    )
}
